import { WppActionButton, WppListItem, WppSelect, WppTypography } from '@wppopen/components-library-react'
import { JSX } from '@wppopen/components-library/dist/types/components'

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState, JSX as JSXReact } from 'react'
import CustomSearch from './CustomSearch'
import Fuse, { FuseResult } from 'fuse.js'
import { getType } from 'utils'
import clsx from 'clsx'
import styles from '../../RenderSelectedProjectRegion/RenderSelectedProjectRegion.module.scss'
import Highlighter from 'react-highlight-words'
import { createRoot, Root } from 'react-dom/client'

interface SelectableItem {
  label: string | number
  title: string
  breadcrumbs: string
}

interface CustomSelectProps<T extends SelectableItem> extends Partial<Omit<JSX.WppSelect, 'withSearch'>> {
  optionsData: T[]
  endSlot?: JSXReact.Element | undefined
  canEdit?: boolean
  children?: ReactNode
}

function CustomSelect<T extends SelectableItem>({
  optionsData,
  endSlot,
  canEdit,
  children,
  ...props
}: CustomSelectProps<T>) {
  const selectRef = useRef<HTMLWppSelectElement>(null)
  const endSlotContainerRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<Root | null>(null)

  const searchTermState = useState<string>('')
  const [searchTerm] = searchTermState
  const [position, setPosition] = useState<number>(0)
  const LIMIT = 25

  // Create the Fuse instance
  const fuse = useMemo(() => {
    const options = {
      // Keys to search in
      keys: [
        { name: 'label', weight: 2 }, // Give Code field higher priority
        'title',
        'breadcrumbs',
      ],
      // Customize matching
      includeScore: true,
      threshold: 0.3, // Lower threshold = more strict matching
      location: 0, // Where to start searching in the string
      distance: 100, // How far to search in the string
      minMatchCharLength: 1, // Minimum characters that must match
      shouldSort: true, // Sort results by score
      findAllMatches: true, // Find all matches, not just the first one
      // For substring matching:
      ignoreLocation: true, // Ignore location and distance for substring matching
    }

    return new Fuse(optionsData, options)
  }, [optionsData])

  const searchResults = useMemo(() => {
    if (!searchTerm)
      return optionsData.map((option, idx) => getType<FuseResult<T>>({ item: option, refIndex: idx, score: 1 }))
    try {
      const results = fuse.search(searchTerm)
      return results
    } catch (error) {
      console.error(error)
    }
  }, [searchTerm])

  const getListElements = useCallback(
    (result: FuseResult<T>) => {
      return (
        <WppListItem
          key={result.item?.label.toString()}
          data-testid="list-option"
          className={styles.listItem}
          value={result.item?.label}
        >
          {' '}
          <div slot="label" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <WppTypography className={clsx(styles.categoryCell__title)} type="s-body" data-testid="gpc-code">
                <Highlighter
                  highlightClassName={styles.highlight}
                  searchWords={[searchTerm]}
                  autoEscape={true}
                  textToHighlight={result.item?.label?.toString()}
                />
              </WppTypography>{' '}
              <Highlighter
                highlightClassName={styles.highlight}
                searchWords={[searchTerm]}
                autoEscape={true}
                textToHighlight={result.item?.title}
              />
            </div>
          </div>
          <div slot="caption">
            <WppTypography tag="p" type="xs-body">
              <Highlighter
                highlightClassName={styles.highlight}
                searchWords={[searchTerm]}
                autoEscape={true}
                textToHighlight={result?.item?.breadcrumbs}
              />
            </WppTypography>
          </div>
        </WppListItem>
      )
    },
    [searchTerm],
  )

  const loadMore = () => {
    setPosition(prev => prev + LIMIT)
  }

  const currentListElement = useMemo(() => {
    if (!searchResults) return null
    const currentValue = optionsData.find(option => Number(option?.label) === Number(props.value))

    const fuseResult: FuseResult<T> = {
      item: currentValue as T,
      refIndex: 0,
    }
    return fuseResult
  }, [optionsData])

  const listItemEls = useMemo(() => {
    if (!searchResults) return null
    // since the results are paginated, when the page initialises, the value returned by the api must be added to the list to be rendered

    const lazyLoadedResults = searchResults?.slice(0, position + LIMIT)
    if (currentListElement && !lazyLoadedResults.find(result => result.item?.label === currentListElement?.item?.label))
      lazyLoadedResults.push(currentListElement)

    return lazyLoadedResults.length ? (
      lazyLoadedResults.map(result => {
        return getListElements(result)
      })
    ) : (
      <WppListItem disabled>
        <div slot="label">None found</div>
      </WppListItem>
    )
  }, [searchResults, getListElements, position, LIMIT])

  useEffect(() => {
    const input = selectRef.current?.shadowRoot?.querySelector('[part="single-select-input"]')
    if (!input || !endSlot) return

    if (!endSlotContainerRef.current) {
      endSlotContainerRef.current = document.createElement('div')

      if (!!input.childNodes.length) {
        const lastChild = input.lastChild
        input.insertBefore(endSlotContainerRef.current, lastChild)
      } else {
        input.appendChild(endSlotContainerRef.current)
      }
    }

    if (!rootRef.current && endSlotContainerRef.current) {
      rootRef.current = createRoot(endSlotContainerRef.current)
    }

    rootRef.current?.render(endSlot)
  }, [endSlot])

  // Hide chevron icon when disabled
  useEffect(() => {
    const input = selectRef.current?.shadowRoot?.querySelector('[part="icon-chevron"]')
    if (input) (input as HTMLElement).style.display = canEdit ? 'block' : 'none'
  }, [canEdit])

  return (
    <WppSelect {...props} withSearch={false} ref={selectRef} type="single" value={Number(props.value)}>
      <CustomSearch searchTermState={searchTermState} handle={props.name!} />
      {listItemEls}
      {(searchResults?.length || 0) - LIMIT > position && (
        <WppActionButton data-testid="load-more-button" className={styles.loadMore} onClick={loadMore}>
          Load more
        </WppActionButton>
      )}
    </WppSelect>
  )
}

export default CustomSelect
